import { SortableTh, SortingDirection, Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { IconButtonRegular as IconButton } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import ASOneIcon from '@components/icons/ASOne.svg';
import HistoryIcon from '@components/icons/History.svg';
import PencilIcon from '@components/icons/Pencil.svg';
import { PagePagination } from '@components/core-ui/Pagination';
import { JobStatusText } from '@components/JobStatusText';
import { Tooltip } from '@components/core-ui/Tooltip';
import { convertDuration } from '@helpers/convertDuration';
import { formatDateMsDsYHI } from '@helpers/formatDateMsDsYHI';
import { formatIsoDateToAmerican } from '@helpers/formatIsoDateToAmerican';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { jobCanBeReassigned } from '@helpers/jobStatus/jobCanBeReassigned';
import { jobIsEditable } from '@helpers/jobStatus/jobIsEditable';
import { jobIsScheduled } from '@helpers/jobStatus/jobIsScheduled';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import { JobResponseDto } from 'app/API';
import { TPaginatedResponse } from 'app/types/API/TPaginatedResponse';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { REPORTING_TYPE_ENUM } from 'app/types/enums/reportingTypes';
import React, { useState } from 'react';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { repositoryJobsListDefaults } from '../../constants/repositoryJobsListDefaults';
import { JobStatusActionsButton } from './JobStatusActionsButton';
import { AssignCell } from './styles/AssignCell';
import { AssignLink } from './styles/AssignLink';
import { DeadlineCell } from './styles/DeadlineCell';
import { IconsWrapper } from './styles/IconsWrapper';
import { IdField } from './styles/IdField';
import { JobName } from './styles/JobName';
import { StatusCell } from './styles/StatusCell';
import { StatusCellContent } from './styles/StatusCellContent';
import { StatusDate } from './styles/StatusDate';
import { StyledLink } from './styles/StyledLink';
import { TagsField } from './styles/TagsField';
import { ButtonsTRow } from '@pages/User/JobsListPage/components/Content/components/JobsTable/ButtonsTRow';
import { canShowButtonsTRow } from '@pages/User/JobsListPage/components/Content/components/JobsTable/helpers/canShowButtonsTRow';
import ChevronLeft from '@components/icons/ChevronLeft.svg';

type TProps = {
  jobs: TPaginatedResponse<JobResponseDto>;
  reload: () => Promise<void>;
};

export const JobsTable = ({ jobs, reload }: TProps) => {
  const { workspace, me, organization } = useAuthContext();
  const { openModal, closeModal } = useModalContext();
  const { page, pageSize, orderBy, orderType, setPage, setPageSize, toggleSort, setSearch } = useListQueryParams({
    defaults: repositoryJobsListDefaults,
  });
  const sortingField = orderBy as keyof JobResponseDto;
  const sortingReverse = orderType === 'desc';
  const [hiddenButtonsVisible, setHiddenButtonsVisible] = useState<string | null>(null);

  const isVsAdmin = me?.roles?.includes('ROLE_VS_ADMIN');

  const canViewAllJobsHistory = organization?.permissions?.viewJobs === 'all';
  const canManageWorkspaces = organization?.permissions?.manageWorkspaces;

  const editAssignee = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.AssignJobModal, {
      onSuccess: () => {
        void reload();
        closeModal();
      },
      job,
    });
  };

  const editDeadline = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.EditDeadline, {
      onSuccess: () => {
        void reload();
        closeModal();
      },
      job,
    });
  };

  const handleShowHistory = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.JobHistory, {
      jobId: job.id,
    });
  };

  const generateJobLink = (jobId: string, jobStatus: JobResponseDto['status']) => {
    if (jobIsEditable(jobStatus)) {
      return routes.jobEdit.make(organization?.slug ?? '', workspace?.slug ?? '', jobId);
    }

    return routes.jobDetails.make(organization?.slug ?? '', workspace?.slug ?? '', jobId);
  };

  return (
    <>
      <TableWrapper>
        <Table>
          <Thead>
            <Tr>
              <SortableTh onClick={() => toggleSort('name')}>
                Job Title
                {sortingField === 'name' && <SortingDirection reverse={sortingReverse} />}
              </SortableTh>
              <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('deadline')}>
                Due
                {sortingField === 'deadline' && <SortingDirection reverse={sortingReverse} />}
              </SortableTh>
              <Th style={{ width: '1%' }}>ASSIGNEE</Th>
              <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('createdAt')}>
                Created
                {sortingField === 'createdAt' ? <SortingDirection reverse={sortingReverse} /> : null}
              </SortableTh>
              <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('duration')}>
                Duration
                {sortingField === 'duration' ? <SortingDirection reverse={sortingReverse} /> : null}
              </SortableTh>
              <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('status')}>
                Status
                {sortingField === 'status' ? <SortingDirection reverse={sortingReverse} /> : null}
              </SortableTh>
              <Th style={{ width: '1%' }} />
              <Th style={{ width: '1%' }} />
            </Tr>
          </Thead>
          <Tbody>
            {jobs.data?.map((job: any, index: number) => {
              const showEditAssigneeButton = organization?.permissions?.reassignJobs && jobCanBeReassigned(job.status);
              const assignee = job?.assignee || job?.draftAssignee;
              const showHistory = canViewAllJobsHistory || me?.id === job.assignee?.id;
              const cellClass = showHistory ? 'with-history' : '';
              const showASOneButton = !job.asOneLocked && jobIsScheduled(job.status);

              const getASOneLinkPrefix = () => {
                return `${window.location.origin}/as-one/job/`;
              };

              return (
                <React.Fragment key={job.id ?? index}>
                  <Tr>
                    <Td>
                      <div>
                        <JobName data-testid={`jobTitle${job.name}`}>
                          <StyledLink to={generateJobLink(job.id, job.status)}>{job.name || 'Unnamed'}</StyledLink>
                        </JobName>
                        <IdField>{job.id}</IdField>
                        <TagsField>
                          {(job.tags ?? []).map((tag: string, i: number) => {
                            return (
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSearch(`#${tag}`);
                                }}
                                key={`${job.id}-${i}`}
                              >
                                #{tag}
                              </a>
                            );
                          })}
                        </TagsField>
                      </div>
                    </Td>
                    <Td>
                      <DeadlineCell>
                        {!job.deadline && <p className="deadLine"></p>}
                        {job.deadline && <p className="deadLine">{formatIsoDateToAmerican(job.deadline)}</p>}
                        {organization?.permissions?.submitJobs ? (
                          <PencilIcon
                            className="text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                            onClick={() => editDeadline(job)}
                          />
                        ) : null}
                      </DeadlineCell>
                    </Td>
                    <Td>
                      <AssignCell>
                        {!assignee ? <div className="assignee"></div> : null}
                        {assignee ? (
                          <Tooltip key={index} content={assignee?.email || ''} placement="bottom">
                            <div className="assignee">
                              <AssignLink to={routes.userDetails.make(organization?.slug ?? '', assignee?.id)}>
                                <p className="assignName">
                                  {assignee?.name} {assignee?.lastname}
                                </p>
                                <p className="assignEmail">{assignee?.email || ''}</p>
                              </AssignLink>
                            </div>
                          </Tooltip>
                        ) : null}
                        {showEditAssigneeButton ? (
                          <PencilIcon
                            className="text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                            onClick={() => editAssignee(job)}
                          />
                        ) : null}
                      </AssignCell>
                    </Td>
                    <Td>
                      <p>{getAmericanTime(job.createdAt)}</p>
                    </Td>
                    <Td>
                      <p>
                        {job.reportingType === REPORTING_TYPE_ENUM.REPORTING_TYPE_FTR
                          ? 'To be analyzed'
                          : convertDuration(Number(job.duration))}
                      </p>
                    </Td>
                    <Td>
                      <StatusCell className={cellClass}>
                        <StatusCellContent>
                          <JobStatusText testid={`jobStatus${job.name}`} status={job.status} removed={job?.removed} />
                          <StatusDate>{formatDateMsDsYHI(job.statusChangeDate ?? '')}</StatusDate>
                        </StatusCellContent>
                        {showHistory ? (
                          <IconButtonClean onClick={() => handleShowHistory(job)}>
                            <HistoryIcon />
                          </IconButtonClean>
                        ) : null}
                      </StatusCell>
                    </Td>
                    <Td>
                      <JobStatusActionsButton job={job} reload={reload} />
                    </Td>
                    <Td style={{ width: '1%' }}>
                      <IconsWrapper>
                        {showASOneButton ? (
                          <IconButton
                            title="Open in AutoScriptOne"
                            onClick={() =>
                              window.open(`${getASOneLinkPrefix()}${job.meetingId}`, '_blank', 'noopener,noreferrer')
                            }
                          >
                            <ASOneIcon />
                          </IconButton>
                        ) : null}
                        {canShowButtonsTRow(job, isVsAdmin, !!canManageWorkspaces, organization, me) ? (
                          <>
                            <IconButton
                              title="Open additional actions"
                              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.preventDefault();
                                setHiddenButtonsVisible(hiddenButtonsVisible === job.id ? null : job.id);
                              }}
                            >
                              <ChevronLeft
                                className={`transition-all ${hiddenButtonsVisible === job.id ? 'rotate-90' : '-rotate-90'}`}
                              />
                            </IconButton>
                          </>
                        ) : null}
                      </IconsWrapper>
                    </Td>
                  </Tr>

                  {hiddenButtonsVisible === job.id ? (
                    <ButtonsTRow job={job} reload={reload} skipFirstRow={true} />
                  ) : null}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </TableWrapper>

      <PagePagination
        totalItemsCount={jobs?.count ?? 0}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </>
  );
};
